import devicesModalStyle from "../devices-modal.module.css";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col } from "react-bootstrap";

// Controller
import controller from "../controller";

// Hook
import useCountry from "hooks/useCountry";
import usePageContext from "hooks/usePageContext";

// Package
import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty, startsWith } from "lodash";

// UI components
import Input from "components/Input";
import Button from "components/Button";
import LinkerWithA from "components/LinkerWithA";
import RadioButton from "components/RadioButton";
import TooltipDialog from "components/TooltipDialog";
import ModalContainer from "components/ModalContainer";
import DropdownWithItem from "components/DropdownWithItem";
import DropdownWithAdvancedSearch from "components/DropdownWithAdvancedSearch";
import SpinnerForModal from "ui/SpinnerForModal";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";
import { deviceSettingActions } from "stores/slice/deviceSetting";
import { expandSiteAndTag } from "ui/Layout/utils";

// Const
import {
  deivceStatusList,
  licenseTermList,
  defaultMessages,
  licenseListLimit,
} from "../const";

const AssignToModal = (props) => {
  const {
    modalStatus,
    changeModalStatus,
    submitModal,
    isOrgLevel,
    filterData,
    pageLimit,
    orgDeviceListFetch,
    orgListLimit,
    orgListFetch,
    seachOrgskip,
    setSeachOrgskip,
    formatDeviceListTargetData,
    parentCheckedIDs,
    orgId,
    assignLicenseToExisting,
    assignLicenseToNew,
  } = props;
  // Assign to local state
  const [assignToMode, setAssignToMode] = useState("existing");
  // Local state(reset when cancel)
  const [searchOrgkey, setSearchOrgkey] = useState("");
  const [searchOrgId, setSearchOrgId] = useState("");
  // const [assignToDevice, setAssignToDevice] = useState({});
  const [selectedSite, setSelectedSite] = useState({});
  const [dropdownSiteList, setDropdownSiteList] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState({});
  const [dropdownProfileList, setDropdownProfileList] = useState([]);
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState({
    ...deivceStatusList[0],
  });
  const [selectedLicenseTerms, setSelectedLicenseTerms] = useState({
    ...licenseTermList[0],
  });
  const [seachLicenseSkip, setSearchLicenseSkip] = useState(0);
  const [selectedOrgDeviceGroups, setSelectedOrgDeviceGroups] = useState({});
  const [licenseList, setLicenseList] = useState([]);
  const [totalLicenseCount, setTotalLicenseCount] = useState(0);
  const [fetchedLicenseCount, setFetchedLicenseCount] = useState(0);
  const [selectedLicense, setSelectedLicense] = useState("");
  const [newOrganization, setNewOrganization] = useState("");
  const [newSite, setNewSite] = useState("");
  const [newAddress, setNewAddress] = useState("");

  const [messages, setMessages] = useState(cloneDeep(defaultMessages));
  const { t } = useTranslation();
  const showModalSpinner = useSelector(selectShowModalSpinner);
  const orgList = useSelector((state) => state.deviceSetting.orgList);
  const orgCount = useSelector((state) => state.deviceSetting.orgCount);
  const fetchedOrgCount = useSelector(
    (state) => state.deviceSetting.fetchedOrgCount
  );
  const {
    countryList,
    selectedCountry,
    changeCountry,
    changeCity,
    setDefaultCountry,
  } = useCountry();
  const { pageContext } = usePageContext();
  const dispatch = useDispatch();
  const dpRef = useRef();

  const assignToDevices = () => {
    const matchedDevice = formatDeviceListTargetData.filter((item) => {
      return item.id === parentCheckedIDs[0];
    });
    const deviceType = matchedDevice[0]
      ? matchedDevice[0].productCategory.toUpperCase()
      : "";
    const modelName = matchedDevice[0] ? matchedDevice[0].modelName : "";
    return { type: deviceType, modelName: modelName };
  };

  const toTargetCountry = (e) => {
    const key = e.key.toUpperCase();
    let targetIndex = 0;
    for (const [index, option] of countryList.country.entries()) {
      if (startsWith(option.title, key)) {
        targetIndex = index;
        break;
      }
    }
    const offsetTop = dpRef.current.children[targetIndex].offsetTop;
    dpRef.current.scrollTo({ top: offsetTop });
  };

  const resetInputs = () => {
    changeModalStatus("assignTo", false);
    setAssignToMode("existing");
    setSearchOrgkey("");
    setSearchOrgId("");
    setSelectedSite({});
    setDropdownSiteList([]);
    setSelectedProfile({});
    setDropdownProfileList([]);
    setSelectedOrgDeviceGroups({});
    setLicenseList([]);
    setSearchLicenseSkip(0);
    setTotalLicenseCount(0);
    setFetchedLicenseCount(0);
    setSelectedLicense("");
    setSelectedDeviceStatus({ ...deivceStatusList[0] });
    setSelectedLicenseTerms({ ...licenseTermList[0] });
    setNewOrganization("");
    setNewSite("");
    setNewAddress("");
    setDefaultCountry({ country: { title: "Afghanistan" } });
  };

  const getLicense = useCallback(
    async (skipLimit) => {
      try {
        const params = {
          active: true,
          bindDevice: false,
          licenseDeviceType: assignToDevices().type,
          limit: 10,
          orderByRemaning: "ASC",
          organizationId: orgId,
          skip: skipLimit,
        };
        const res = await controller.getLicenseApi(params);
        if (skipLimit === 0) setSelectedLicense(res.data[0].key);
        setLicenseList([...licenseList, ...res.data]);
        setTotalLicenseCount(res.total);
        setFetchedLicenseCount(fetchedLicenseCount + res.data.length);
      } catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });
      }
    },
    [
      dispatch,
      formatDeviceListTargetData,
      messages,
      orgId,
      parentCheckedIDs,
      fetchedLicenseCount,
      licenseList,
    ]
  );

  const setSiteProfile = (siteObj, profileObj) => {
    const siteList = Object.entries(siteObj)
      .map(([key, value]) => {
        return { title: value.name, id: key };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    if (siteList.length > 0) {
      setSelectedSite(siteList[0]);
      setDropdownSiteList(siteList);
      const profileList = controller.getProfileList(
        cloneDeep(profileObj),
        siteList[0].id,
        assignToDevices()
      );
      setSelectedProfile(profileList[0]);
      setDropdownProfileList(profileList);
    } else {
      setSelectedSite({});
      setDropdownSiteList([]);
      setSelectedProfile({});
      setDropdownProfileList([]);
    }
    setSelectedOrgDeviceGroups(cloneDeep(profileObj));
  };

  const init = () => {
    if (isOrgLevel) {
      //getBootstrap from pageContext
      setSiteProfile(
        pageContext.selectedOrg.storeObj,
        pageContext.selectedOrg.deviceGroups
      );
    }
    getLicense(0);
  };

  useEffect(() => {
    if (modalStatus.assignTo.status) {
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [modalStatus.assignTo.status]);

  const getBootstrap = async (orgId) => {
    dispatch(uiActions.showModalSpinner());
    try {
      const response = await controller.getOrgBootstrapApi(orgId);
      if (response.error) {
        throw response.error;
      }
      let orgObj = {
        selectedOrg: response,
        selectedStoreTag: null,
        selectedStore: null,
        orgTreeMap: null,
      };
      orgObj = await expandSiteAndTag(orgObj);

      setSiteProfile(
        orgObj.selectedOrg.storeObj,
        orgObj.selectedOrg.deviceGroups
      );

      setMessages(cloneDeep(defaultMessages));
    } catch (error) {
      setMessages({
        ...messages,
        error: error.code,
        success: null,
      });
    } finally {
      dispatch(uiActions.hideModalSpinner());
    }
  };

  const checkIfDisabled = () => {
    if (assignToMode === "existing") {
      return selectedDeviceStatus.value === "Unused"
        ? !(
            (isOrgLevel || searchOrgkey) &&
            !isEmpty(selectedSite) &&
            !isEmpty(selectedProfile) &&
            selectedDeviceStatus &&
            selectedLicenseTerms
          )
        : !(
            (isOrgLevel || searchOrgkey) &&
            !isEmpty(selectedSite) &&
            !isEmpty(selectedProfile) &&
            selectedDeviceStatus &&
            selectedLicense
          );
    } else {
      return selectedDeviceStatus.value === "Unused"
        ? !(
            (isOrgLevel || newOrganization) &&
            newSite &&
            selectedCountry?.country &&
            selectedCountry?.city &&
            selectedDeviceStatus &&
            selectedLicenseTerms
          )
        : !(
            (isOrgLevel || newOrganization) &&
            newSite &&
            selectedCountry?.country &&
            selectedCountry?.city &&
            selectedDeviceStatus &&
            selectedLicense
          );
    }
  };

  return (
    <>
      <div className={devicesModalStyle["inventory-assign-to-modal"]}>
        <ModalContainer
          modalWidthType="modal-500px"
          openModal={modalStatus.assignTo.status}
          closeModal={() => resetInputs()}
        >
          {showModalSpinner && <SpinnerForModal />}
          <div className="header">
            <div className="title">{t("ba8cfdf51c")}</div>
          </div>
          <div className="body">
            {/* Existing site and profile */}
            <div>
              <div className="modal-form-title d-inline-flex">
                <RadioButton
                  id="existingOrg"
                  name="existingOrg"
                  label={t("2966b4acaa")}
                  checked={assignToMode === "existing"}
                  labelClassName="form-title"
                  onChange={() => setAssignToMode("existing")}
                />
                <TooltipDialog
                  className="ms-2"
                  placement="right"
                  title={t("8b1f269f84")}
                ></TooltipDialog>
              </div>
            </div>

            {/* Existing site and profile content */}
            <div
              className={`mb-1 ms-3 ${
                devicesModalStyle["existing-org-container"]
              } ${assignToMode === "existing" ? "d-block" : "d-none"}`}
            >
              <Row>
                {!isOrgLevel && (
                  <Col>
                    <div className={devicesModalStyle["select-org-container"]}>
                      <div className="modal-form-title required">
                        {t("d6b258792f")}
                      </div>
                      <DropdownWithAdvancedSearch
                        value={searchOrgkey}
                        buttonIcon={"enter"}
                        dataBsToggleOnInput={true}
                        subContainerOnClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setSearchOrgkey(e.target.value);
                        }}
                        onClick={(e) => {
                          if (e.target.value.trim() === "") {
                            setSeachOrgskip(0);
                            setSearchOrgkey("");
                            dispatch(deviceSettingActions.cleanOrg());
                            orgListFetch(encodeURI("%"), 0);
                          } else {
                            setSeachOrgskip(0);
                            setSearchOrgkey("");
                            setSelectedSite({});
                            setSelectedProfile({});
                            dispatch(deviceSettingActions.cleanOrg());
                            orgListFetch(encodeURI("%"), 0);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.target.value.trim() !== "" &&
                            e.key === "Enter"
                          ) {
                            setSeachOrgskip(0);
                            dispatch(deviceSettingActions.cleanOrg());
                            orgListFetch(e.target.value, 0);
                          }
                        }}
                      >
                        <div className={devicesModalStyle["more-org"]}>
                          <div className={devicesModalStyle["show-more-info"]}>
                            {t("3a6d32cc65", { count: fetchedOrgCount })}{" "}
                            {orgCount} {t("cfb453439b")}.
                          </div>
                          <LinkerWithA
                            label={
                              orgCount > seachOrgskip + orgListLimit
                                ? t("2a725a2a3d")
                                : ""
                            }
                            href="#"
                            className="linker-blue show-more text-decoration-underline"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSeachOrgskip(seachOrgskip + orgListLimit);
                              orgListFetch(
                                searchOrgkey === ""
                                  ? encodeURI("%")
                                  : searchOrgkey,
                                seachOrgskip + orgListLimit
                              );
                            }}
                          ></LinkerWithA>
                        </div>
                        {orgList &&
                          orgList.map((org, index) => {
                            return (
                              <li
                                key={index}
                                className="search-org-item"
                                value={org.name}
                                id={org.id}
                                onClick={(e) => {
                                  setSearchOrgkey(e.target.innerText);
                                  setSearchOrgId(e.target.id);
                                  getBootstrap(e.target.id);
                                }}
                              >
                                {org.name}
                              </li>
                            );
                          })}
                      </DropdownWithAdvancedSearch>
                    </div>
                  </Col>
                )}
                <Col sm={6}>
                  <div className="modal-form-title required">
                    {t("a7d6475ec8")}
                  </div>
                  <DropdownWithItem
                    id="site-dropdown"
                    type="normal"
                    selectedItem={selectedSite}
                    itemList={dropdownSiteList}
                    onClick={(item) => {
                      setSelectedSite(item);
                      const profileList = controller.getProfileList(
                        selectedOrgDeviceGroups,
                        item.id,
                        assignToDevices()
                      );
                      setSelectedProfile(profileList[0]);
                      setDropdownProfileList(profileList);
                    }}
                  />
                </Col>
              </Row>

              <Row>
                <Col sm={6}>
                  <div className="modal-form-title required mt-3">
                    {t("cce99c598c")}
                  </div>
                  <DropdownWithItem
                    id="profile-dropdown"
                    type="normal"
                    selectedItem={selectedProfile}
                    itemList={dropdownProfileList}
                    onClick={(item) => {
                      setSelectedProfile(item);
                    }}
                  />
                </Col>
              </Row>

              <div className="mt-3">
                <span className="modal-form-title">{t("8fccc52ef8")}</span>
                <span className="ms-1">
                  <TooltipDialog
                    className="ms-2"
                    placement="right"
                    title={t("989b3e36f1")}
                  ></TooltipDialog>
                </span>
              </div>

              <div className="modal-form-title required mt-3">
                {t("9ec073dd2e")}
              </div>
              <Row>
                <Col>
                  <DropdownWithItem
                    id="licese-status-dropdown"
                    type="normal"
                    selectedItem={selectedDeviceStatus}
                    itemList={deivceStatusList}
                    onClick={(item) => {
                      setSelectedDeviceStatus(item);
                    }}
                  />
                </Col>
                <Col>
                  {selectedDeviceStatus.value === "Unused" ? (
                    <DropdownWithItem
                      id="license-term-dropdown"
                      type="normal"
                      selectedItem={selectedLicenseTerms}
                      itemList={licenseTermList}
                      onClick={(item) => {
                        setSelectedLicenseTerms(item);
                      }}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={selectedLicense}
                      onChange={(e) => {
                        setSelectedLicense(e.target.value);
                      }}
                      onClick={() => {
                        setSelectedLicense("");
                      }}
                      dataBsToggleOnInput={true}
                      subContainerOnClick={() => {}}
                    >
                      <>
                        <div className="more-content">
                          <div className="show-more-info">
                            {t("3a6d32cc65", { count: fetchedLicenseCount })}{" "}
                            {totalLicenseCount} {t("cfb453439b")}.
                          </div>
                          <LinkerWithA
                            label={
                              totalLicenseCount >
                              seachLicenseSkip + licenseListLimit
                                ? t("2a725a2a3d")
                                : ""
                            }
                            href="#"
                            className="linker-blue show-more-btn text-decoration-underline"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSearchLicenseSkip(
                                seachLicenseSkip + licenseListLimit
                              );
                              dispatch(uiActions.showModalSpinner());
                              getLicense(seachLicenseSkip + licenseListLimit);
                              setTimeout(() => {
                                dispatch(uiActions.hideModalSpinner());
                              }, 1000);
                            }}
                          ></LinkerWithA>
                        </div>
                        {licenseList &&
                          licenseList.map((license, index) => {
                            return (
                              <li
                                key={index}
                                className="search-org-item"
                                value={license.key}
                                onClick={(e) => {
                                  setSelectedLicense(
                                    e.target.innerText
                                      .replace(/\(.*\)/, "")
                                      .trim()
                                  );
                                }}
                              >
                                {license.key}&nbsp;(
                                {Math.round(license.timeRemaining / 86400)}
                                &nbsp;
                                {t("44fdec4703")})
                              </li>
                            );
                          })}
                      </>
                    </Input>
                  )}
                </Col>
              </Row>
            </div>

            {/* New organization, site, and profile */}
            <div className="mt-3">
              <div className="modal-form-title d-inline-flex">
                <RadioButton
                  id="newOrg"
                  name="newOrg"
                  label={t("d708f9c735")}
                  checked={assignToMode === "new"}
                  labelClassName="form-title"
                  onChange={() => {
                    setAssignToMode("new");
                  }}
                />
                <TooltipDialog
                  className="ms-2"
                  placement="right"
                  title="You may assign multiple devices to an new created organization and site. <br>Nuclias cloud will use the default configuration as your default profile. <br>You may modify profile configuration in the organization later."
                ></TooltipDialog>
              </div>
            </div>

            {/* New organization, site, and profile content */}
            <div
              className={`mb-1 ms-3 ${devicesModalStyle["new-org-container"]} ${
                assignToMode === "new" ? "d-block" : "d-none"
              } mt-1`}
            >
              <Row>
                {!isOrgLevel && (
                  <Col>
                    <div className="modal-form-title required">
                      {t("d6b258792f")}
                    </div>
                    <Input
                      type="text"
                      value={newOrganization}
                      onChange={(e) => {
                        setNewOrganization(e.target.value);
                      }}
                      onFocus={() => {}}
                      onBlur={() => {}}
                    />
                  </Col>
                )}

                <Col sm={6}>
                  <div className="modal-form-title required">
                    {t("a7d6475ec8")}
                  </div>
                  <Input
                    type="text"
                    value={newSite}
                    onChange={(e) => {
                      setNewSite(e.target.value);
                    }}
                    onFocus={() => {}}
                    onBlur={() => {}}
                  />
                </Col>
              </Row>
              <Row className="d-flex align-items-end mt-1">
                <Col>
                  <div className="modal-form-title">{t("e7d89871c4")}</div>
                  <DropdownWithItem
                    id="country-dropdown"
                    type="normal"
                    selectedItem={selectedCountry?.country || {}}
                    itemList={countryList?.country || []}
                    onClick={(item) => {
                      changeCountry(item);
                    }}
                    onKeyDown={(e) => {
                      // match the first one and select it
                      const firstMatch = (countryList?.country || []).find(
                        (country) =>
                          country.title.charAt(0).toLowerCase() ===
                          e.key.toLowerCase()
                      );
                      if (firstMatch) {
                        changeCountry(firstMatch);
                        toTargetCountry(e);
                      }
                    }}
                    dropDownMenuStyle={{ maxHeight: "300px", overflow: "auto" }}
                    dropDownMenuRef={dpRef}
                  />
                </Col>
                <Col>
                  <DropdownWithItem
                    id="time-zone-dropdown"
                    type="normal"
                    selectedItem={selectedCountry?.city || {}}
                    itemList={countryList?.city || []}
                    onClick={(item) => {
                      changeCity(item);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <div className="modal-form-title">{t("dd7bf230fd")}</div>
                  <textarea
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <div className="modal-form-title">{t("cce99c598c")}</div>
                  <div className={devicesModalStyle["profile-hint"]}>
                    {t("8755561dff")}
                  </div>
                </Col>
              </Row>

              <div className="mt-3">
                <span className="modal-form-title">Auto-assign license</span>
                <span className="ms-1">
                  <TooltipDialog
                    className="ms-2"
                    placement="right"
                    title={t("989b3e36f1")}
                  ></TooltipDialog>
                </span>
              </div>

              <div className="modal-form-title required mt-3">
                {t("9ec073dd2e")}
              </div>
              <Row>
                <Col>
                  <DropdownWithItem
                    id="licese-status-dropdown"
                    type="normal"
                    selectedItem={selectedDeviceStatus}
                    itemList={deivceStatusList}
                    onClick={(item) => {
                      setSelectedDeviceStatus(item);
                    }}
                  />
                </Col>
                <Col>
                  {selectedDeviceStatus.value === "Unused" ? (
                    <DropdownWithItem
                      id="license-term-dropdown"
                      type="normal"
                      selectedItem={selectedLicenseTerms}
                      itemList={licenseTermList}
                      onClick={(item) => {
                        setSelectedLicenseTerms(item);
                      }}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={selectedLicense}
                      onChange={(e) => {
                        setSelectedLicense(e.target.value);
                      }}
                      onClick={() => {
                        setSelectedLicense("");
                      }}
                      dataBsToggleOnInput={true}
                      subContainerOnClick={() => {}}
                    >
                      <>
                        <div className="more-content">
                          <div className="show-more-info">
                            {t("3a6d32cc65", { count: fetchedLicenseCount })}{" "}
                            {totalLicenseCount} {t("cfb453439b")}.
                          </div>
                          <LinkerWithA
                            label={
                              totalLicenseCount >
                              seachLicenseSkip + licenseListLimit
                                ? t("2a725a2a3d")
                                : ""
                            }
                            href="#"
                            className="linker-blue show-more-btn text-decoration-underline"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSearchLicenseSkip(
                                seachLicenseSkip + licenseListLimit
                              );
                              dispatch(uiActions.showModalSpinner());
                              getLicense(seachLicenseSkip + licenseListLimit);
                              setTimeout(() => {
                                dispatch(uiActions.hideModalSpinner());
                              }, 1000);
                            }}
                          ></LinkerWithA>
                        </div>
                        {licenseList &&
                          licenseList.map((license, index) => {
                            return (
                              <li
                                key={index}
                                className="search-org-item"
                                value={license.key}
                                onClick={(e) => {
                                  setSelectedLicense(
                                    e.target.innerText
                                      .replace(/\(.*\)/, "")
                                      .trim()
                                  );
                                }}
                              >
                                {license.key}&nbsp;(
                                {Math.round(license.timeRemaining / 86400)}
                                &nbsp;
                                {t("44fdec4703")})
                              </li>
                            );
                          })}
                      </>
                    </Input>
                  )}
                </Col>
              </Row>
            </div>
          </div>
          <div className={`footer ${devicesModalStyle["footer"]}`}>
            <Button
              label="Cancel"
              className="btn-cancel"
              disabled={false}
              onClick={() => resetInputs()}
            />
            <Button
              label={
                assignToMode === "existing" ? t("5759d8b722") : t("bb5afaf984")
              }
              className="btn-submit"
              disabled={checkIfDisabled()}
              onClick={() => {
                if (assignToMode === "existing") {
                  const selectedOrgId = !isOrgLevel ? searchOrgId : orgId;
                  assignLicenseToExisting(
                    cloneDeep({
                      selectedOrgId,
                      selectedSite,
                      selectedProfile,
                      selectedDeviceStatus,
                      selectedLicenseTerms,
                      selectedLicense,
                    })
                  );
                } else {
                  assignLicenseToNew(
                    cloneDeep({
                      newOrganization,
                      newSite,
                      selectedCountry,
                      newAddress,
                      selectedDeviceStatus,
                      selectedLicenseTerms,
                      selectedLicense,
                    })
                  );
                }
                resetInputs();
              }}
            />
          </div>
        </ModalContainer>
      </div>
    </>
  );
};

export default AssignToModal;
